/**
 * SearchableSelect - A select component with built-in search filtering
 *
 * Uses the existing Select component but adds a search input at the top
 * to filter options. Useful when there are many items to choose from.
 */

import { useMemo, useState } from 'react';
import { Icon } from '@/components/ui/icon';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

export interface SearchableSelectOption {
  value: string;
  label: string;
  description?: string;
  searchTerms?: string; // Additional terms to match against
}

interface SearchableSelectProps {
  value: string;
  onValueChange: (value: string) => void;
  options: SearchableSelectOption[];
  placeholder?: string;
  searchPlaceholder?: string;
  disabled?: boolean;
  isLoading?: boolean;
  loadingText?: string;
  emptyText?: string;
  className?: string;
}

export function SearchableSelect({
  value,
  onValueChange,
  options,
  placeholder = 'Select an option',
  searchPlaceholder = 'Search...',
  disabled = false,
  isLoading = false,
  loadingText = 'Loading...',
  emptyText = 'No results found',
  className,
}: SearchableSelectProps) {
  const [search, setSearch] = useState('');

  // Filter options based on search term
  const filteredOptions = useMemo(() => {
    if (!search.trim()) return options;

    const searchLower = search.toLowerCase();
    return options.filter((option) => {
      const labelMatch = option.label.toLowerCase().includes(searchLower);
      const descMatch = option.description?.toLowerCase().includes(searchLower);
      const termsMatch = option.searchTerms?.toLowerCase().includes(searchLower);
      return labelMatch || descMatch || termsMatch;
    });
  }, [options, search]);

  const selectedOption = options.find((o) => o.value === value);

  return (
    <Select value={value} onValueChange={onValueChange} disabled={disabled || isLoading}>
      <SelectTrigger className={className}>
        <SelectValue placeholder={isLoading ? loadingText : placeholder}>
          {selectedOption?.label || placeholder}
        </SelectValue>
      </SelectTrigger>
      <SelectContent>
        {/* Search input - prevents select from closing on click */}
        <div
          className="px-2 py-1.5 sticky top-0 bg-white dark:bg-neutral-900 z-10"
          onKeyDown={(e) => e.stopPropagation()}
        >
          <div className="relative">
            <Icon
              name="search"
              className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400"
            />
            <Input
              type="text"
              placeholder={searchPlaceholder}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-8 h-8 text-sm"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>

        {/* Options */}
        {filteredOptions.length === 0 ? (
          <div className="py-6 text-center text-sm text-neutral-500">
            <Icon name="search_off" className="h-5 w-5 mx-auto mb-1 text-neutral-400" />
            {emptyText}
          </div>
        ) : (
          filteredOptions.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              <div className="flex flex-col">
                <span>{option.label}</span>
                {option.description && (
                  <span className="text-xs text-neutral-400">{option.description}</span>
                )}
              </div>
            </SelectItem>
          ))
        )}
      </SelectContent>
    </Select>
  );
}
